import { DataGrid, GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import {
  getDaysRemainingOnWarrantyFromEndDate,
  hasDatePassed,
} from '../../utils/dateTime';
import {
  useFindAllHardwareQuery,
  useFindAllLicensesByCompanyIdQuery,
  useUpdateHardwareMutation,
} from '../../api/api-hooks';
import {
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useSoftwareTypeMap } from '../../hooks/useSoftwareTypeMap';
import { useHistory } from 'react-router-dom';
import { useGridSort } from '../../hooks/useGridSort';
import { Item } from '../../utils/sharedTypes';
import { getIconForSoftwareType } from '../dashboard/dashboardUtils';
import SoftwareSoldCreatorWithDefaults from '../software-sold/SoftwareSoldCreatorWithDefaults';
import React, { useState } from 'react';
import { AddCircleOutline } from '@mui/icons-material';
import { useGetMostRecentSupportPurchase } from '../software-sold/useGetMostRecentSupportPurchase';
import { TrimmedCompany } from '../../api/api-types';

interface Props {
  company: TrimmedCompany;
  setSelectedHardware: React.Dispatch<React.SetStateAction<Item[]>>;
  editMode: boolean;
}

const AdminCompanyHardwareTable = ({
  company,
  setSelectedHardware,
  editMode,
}: Props) => {
  const { data: hardware } = useFindAllHardwareQuery({});
  const { data: companyLicenses } = useFindAllLicensesByCompanyIdQuery({
    id: company.id,
  });
  const { sortModel, handleSortModelChange } = useGridSort('serial', 'asc');
  const [addSoftwareModalOpen, setAddSoftwareModalOpen] = useState(false);
  const [updateHardware] = useUpdateHardwareMutation();
  const [clickedSerial, setClickedSerial] = useState('');
  const typeMap = useSoftwareTypeMap();
  const getMostRecentSupportPurchase = useGetMostRecentSupportPurchase();
  const { push } = useHistory();

  if (hardware === undefined || companyLicenses === undefined)
    return <CircularProgress />;

  // Filter down to company's owned hardware
  const companyHardware = hardware.filter((item) => {
    const companyIdsForItem = item.companies.map((company) => company.id);
    return companyIdsForItem.includes(company.id);
  });

  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'serial', headerName: 'Serial', width: 150 },
    {
      field: 'hardwareType',
      headerName: 'Hardware Type',
      width: 180,
      renderCell: (params) => {
        const hardwareId = params.getValue(params.id, 'id');
        const hardwareForRow = companyHardware.find(
          (item) => item.id === hardwareId
        );
        if (!hardwareForRow) return 'Not available';
        return (
          <Link
            href={`/hardware/${hardwareForRow.hardwareTypeId}`}
            onClick={(event: any) => {
              event.preventDefault();
              push(`/hardware/${hardwareForRow.hardwareTypeId}`);
            }}
            underline='hover'
          >
            {hardwareForRow
              ? hardwareForRow.hardwareType.typeName
              : 'Link to Firmware'}
          </Link>
        );
      },
    },

    {
      field: 'remainingWarranty',
      headerName: 'Warranty Left',
      width: 170,
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 150,
      renderCell: (params) => {
        if (!editMode)
          return params.row.department
            ? params.row.department.departmentName
            : 'None';
        return (
          <FormControl variant='standard' fullWidth>
            <Select
              labelId='select-label'
              id='select'
              value={params.row.department ? params.row.department.id : -1}
              onChange={(e) => {
                updateHardware({
                  id: params.row.id,
                  updateHardwareDto: {
                    id: params.row.id,
                    departmentId:
                      e.target.value !== -1 ? Number(e.target.value) : null,
                  },
                });
              }}
            >
              {company.departments.map((dept) => (
                <MenuItem value={dept.id}>{dept.departmentName}</MenuItem>
              ))}
              <MenuItem value={-1}>None</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'software',
      renderHeader: () => {
        return (
          <Grid container wrap={'nowrap'} spacing={1}>
            <Grid item>
              <Typography variant={'subtitle2'}>Software</Typography>
            </Grid>
            <Grid item>
              <Typography variant={'subtitle2'} color={'textSecondary'}>
                (Click icons to access downloads and documentation)
              </Typography>
            </Grid>
          </Grid>
        );
      },
      width: 500,
      renderCell: (params) => {
        const hardwareId = params.getValue(params.id, 'id');
        const hardwareForRow = companyHardware.find(
          (item) => item.id === hardwareId
        );
        const licenses = companyLicenses.filter(
          (l) => l.hardwareId === hardwareId
        );
        const softwareAlreadyDisplayed: number[] = [];
        return (
          <Grid
            container
            direction='row'
            spacing={1}
            alignItems='center'
            wrap={'nowrap'}
          >
            {/* Filter licenses for hardware down to nonduplicative software types */}
            {licenses
              .filter((license) => {
                if (softwareAlreadyDisplayed.includes(license.softwareId))
                  return false;
                if (!license.moduleId) {
                  softwareAlreadyDisplayed.push(license.softwareId);
                  return true;
                }
                return false;
              })
              .map((license) => {
                const currentSupportRecord =
                  getMostRecentSupportPurchase(license);
                const activeSupport =
                  currentSupportRecord &&
                  !hasDatePassed(new Date(currentSupportRecord.supportEndDate));
                return (
                  <Grid item style={{ height: '45px' }}>
                    <Link
                      href={`/software/${license.softwareId}?company=${company.id}`}
                      onClick={(event: any) => {
                        event.preventDefault();
                        push(
                          `/software/${license.softwareId}?company=${company.id}`
                        );
                      }}
                      underline='hover'
                    >
                      {/* Get URL for icon, prepending an underscore for the grayed-out icon if support has expired */}
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          '/' +
                          (!activeSupport ? '_' : '') +
                          getIconForSoftwareType(
                            typeMap.get(license.softwareId)
                          )
                        }
                        alt={typeMap.get(license.softwareId)}
                        title={typeMap.get(license.softwareId)}
                        height='100%'
                      />
                    </Link>
                  </Grid>
                );
              })}
            {hardwareForRow && (
              <Grid item>
                <IconButton
                  onClick={() => {
                    setClickedSerial(hardwareForRow.serial);
                    setAddSoftwareModalOpen(true);
                  }}
                  size='large'
                >
                  <AddCircleOutline />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      },
    },
  ];
  const rows = companyHardware.map((item) => ({
    ...item,
    department: item.department,
    hardwareType: item.hardwareType.typeName,
    remainingWarranty:
      item.warrantyStartDate === null
        ? 'None'
        : getDaysRemainingOnWarrantyFromEndDate(item.warrantyEndDate),
  }));

  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedHardware(hardware.filter((item) => model.includes(item.id)));
  };

  return (
    <>
      <div style={{ paddingBottom: '1rem', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={100}
          disableSelectionOnClick
          // disableColumnMenu={true}
          checkboxSelection
          autoHeight
          onSelectionModelChange={handleTableSelection}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
        />
      </div>
      <SoftwareSoldCreatorWithDefaults
        company={company}
        serial={clickedSerial}
        dialogOpen={addSoftwareModalOpen}
        setDialogOpen={setAddSoftwareModalOpen}
      />
    </>
  );
};

export default AdminCompanyHardwareTable;
